import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { EventType, InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import type { EventMessage, IPublicClientApplication, RedirectRequest } from '@azure/msal-browser';
import { loginRequest, selectAccountRequest, ssoSilentRequest } from '@/auth/msalConfig';

/**
 * Set immediately before we hand the browser to Entra. If we come back from
 * that round trip still unauthenticated, the redirect has demonstrably failed
 * for this user — conditional access, a disabled account, a cancelled prompt —
 * and repeating it automatically would bounce them between us and
 * login.microsoftonline.com forever with no chance to read the error. This
 * flag is the thing that makes the loop impossible.
 */
const REDIRECT_SENT_KEY = 'kt.rp.signInRedirectSent';

/**
 * Set when the user signs out on purpose. Without it, auto sign-in would sign
 * them straight back in and the Sign out button would look broken. Kept in
 * sessionStorage so it is scoped to the tab, matching MSAL's own cache: a new
 * tab is automatic again.
 */
const SIGNED_OUT_KEY = 'kt.rp.signedOut';

/** sessionStorage throws in some privacy modes; a missing flag must not break sign-in. */
function flag(key: string) {
  return {
    isSet: () => {
      try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
    },
    set: () => {
      try { sessionStorage.setItem(key, '1'); } catch { /* storage unavailable */ }
    },
    clear: () => {
      try { sessionStorage.removeItem(key); } catch { /* storage unavailable */ }
    }
  };
}

const redirectSent = flag(REDIRECT_SENT_KEY);
const signedOut = flag(SIGNED_OUT_KEY);

/**
 * Signs out and suppresses auto sign-in for the rest of this tab session.
 * Call instead of instance.logoutRedirect() directly.
 */
export function signOut(instance: IPublicClientApplication): void {
  signedOut.set();
  redirectSent.clear();
  void instance.logoutRedirect();
}

/**
 * Sends the browser to Entra and arms the loop guard. Every interactive
 * sign-in in the app goes through here so no path can start a redirect the
 * guard does not know about.
 */
export function startSignIn(
  instance: IPublicClientApplication,
  request: RedirectRequest = loginRequest
): Promise<void> {
  signedOut.clear();
  redirectSent.set();
  return instance.loginRedirect(request);
}

/** Sends the browser to Entra with the account picker forced. */
export function chooseAccount(instance: IPublicClientApplication): Promise<void> {
  return startSignIn(instance, selectAccountRequest);
}

export interface AutoSignInBlock {
  reason: 'signedOut' | 'failed';
  /** Underlying Entra/MSAL error, when we managed to capture one. */
  message: string;
}

export interface AutoSignIn {
  authenticated: boolean;
  /** Automatic sign-in has given up; render a manual card. Terminal until the user acts. */
  blocked: AutoSignInBlock | null;
  signIn: () => void;
  chooseAccount: () => void;
}

function errorText(error: unknown): string {
  if (error instanceof Error) return error.message;
  return typeof error === 'string' ? error : '';
}

/**
 * Signs the user in on first load without them clicking anything, and — far
 * more importantly — knows when to stop trying.
 */
export function useAutoSignIn(): AutoSignIn {
  const { instance, inProgress } = useMsal();
  const authenticated = useIsAuthenticated();
  const [blocked, setBlocked] = useState<AutoSignInBlock | null>(null);
  const attempted = useRef(false);
  const redirectError = useRef<string>('');

  const block = useCallback((reason: AutoSignInBlock['reason'], message: string) => {
    // Reuse the previous object when nothing changed so a repeated call cannot
    // re-render its way into a loop.
    setBlocked(prev =>
      prev && prev.reason === reason && prev.message === message ? prev : { reason, message });
  }, []);

  // MsalProvider deliberately swallows handleRedirectPromise rejections and
  // tells callers to listen for the failure event instead, so this is the only
  // place the real AADSTS error from a failed redirect is available to show.
  useEffect(() => {
    const callbackId = instance.addEventCallback((message: EventMessage) => {
      if (message.eventType === EventType.LOGIN_FAILURE) {
        redirectError.current = errorText(message.error);
      }
    });
    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  const startRedirect = useCallback((request: RedirectRequest) => {
    setBlocked(null);
    startSignIn(instance, request).catch(error => block('failed', errorText(error)));
  }, [instance, block]);

  useEffect(() => {
    // Any status other than None means MSAL is mid-flight, including the
    // Startup/HandleRedirect pass where handleRedirectPromise is still settling.
    // Starting a second interaction here is what throws interaction_in_progress.
    if (inProgress !== InteractionStatus.None) return;

    if (authenticated) {
      // The round trip worked, so re-arm both guards for the next cold start.
      redirectSent.clear();
      signedOut.clear();
      setBlocked(null);
      return;
    }

    if (signedOut.isSet()) {
      block('signedOut', '');
      return;
    }

    if (redirectSent.isSet()) {
      // Back from Entra and still not signed in. Terminal by design: the card
      // shows what happened and the user re-arms the flow by clicking.
      block('failed', redirectError.current);
      return;
    }

    // One automatic attempt per page load, whatever MSAL's status does next.
    if (attempted.current) return;
    attempted.current = true;

    instance.ssoSilent(ssoSilentRequest).catch(error => {
      // InteractionRequiredAuthError is Entra saying "this needs a human".
      // Anything else is the browser refusing to do it silently — an iframe
      // blocked by tracking prevention, monitor_window_timeout, a transient
      // network failure — which MSAL's own ssoSilent guidance says to retry as
      // a full-frame redirect. Both go the same way; REDIRECT_SENT_KEY is what
      // keeps either from becoming a loop.
      if (!(error instanceof InteractionRequiredAuthError)) {
        console.warn('Silent SSO failed for a non-interactive reason; falling back to redirect', error);
      }
      startRedirect(loginRequest);
    });
  }, [inProgress, authenticated, instance, block, startRedirect]);

  return {
    authenticated,
    blocked,
    signIn: useCallback(() => startRedirect(loginRequest), [startRedirect]),
    chooseAccount: useCallback(() => startRedirect(selectAccountRequest), [startRedirect])
  };
}
